/**
 * Phase 8 T-06c §21 — Smart Match thread state (localStorage only).
 *
 * Phase-8 scope bans server-side persistence for Smart Match threads. This
 * module owns the shape + CRUD for the single source of truth:
 *
 *   - `ggj.smartmatch.threads`           → array of all threads (history).
 *   - `ggj.smartmatch.recent`            → `{ id, name, createdAt, lastOpenedAt }`
 *                                          used for the sidebar chip. 7-day TTL.
 *
 * The thread record is intentionally minimal — it stores presentation state
 * (name, tab, collection) and metadata only. Candidate rows and agent turns
 * are owned by their own stores (`MatchCanvasLayout` session state for
 * shortlist/hide, `useAgentThread` for chat history). That keeps this module
 * trivially unit-testable and free of side-effects.
 */

export type SmartMatchIntakeTab = 'job-description' | 'refine-search';

/**
 * §29.A — Smart Match four-stage flow:
 *   intake   → user is on `/smart-match/new`.
 *   clarify  → Stage 1 (JD quote card + paraphrase turn + clarifying question).
 *              Thread has advanced past intake but hasn't started sourcing yet.
 *   stream   → Stage 2 (three-column active, skeleton cards, [Stop]).
 *   results  → Stage 3 (cards present; next-step bridge visible).
 *
 * A thread created via the intake page starts at `clarify` (since submit
 * advances past intake). Stream → results is driven by completion signals
 * from `useMatchResults` / the streaming stub.
 */
export type SmartMatchStage = 'intake' | 'clarify' | 'stream' | 'results';

/**
 * §29.E / §29.F — which intake body created this thread. `'single'` is the
 * default for threads predating T-26. Drives Stage 1/2/3 variant selection
 * across the Smart Match surface.
 */
export type SmartMatchMode = 'single' | 'multi' | 'bulk';

/**
 * Which surface created the thread. Absent ⇒ `'smart-match'` (pre-Sourcing
 * threads). `'sourcing'` threads are authored from `/sourcing/*` and rendered
 * by the Sourcing history column + canvas; they share the same store so a
 * user can promote a Sourcing conversation to Smart Match without a copy.
 */
export type SmartMatchOrigin = 'smart-match' | 'sourcing';

export interface SmartMatchCollectionRef {
  id: string;
  name: string;
}

/** §29.E Multi-JD — a single JD in the list submitted with the thread. */
export interface SmartMatchJdEntry {
  id: string;
  title: string;
  text: string;
  /** §29.C Stop semantics — marks this JD as cancelled mid-stream. */
  cancelled?: boolean;
}

/**
 * §29.F Bulk-resumes — uploaded-file metadata persisted on the thread.
 * File bytes are NEVER persisted — we keep filename / size / parse-status
 * only. The parsed text is held in-memory by the intake/Stage-1 surfaces
 * and forwarded to the scoring stub through props, not localStorage.
 */
export interface SmartMatchResumeEntry {
  id: string;
  name: string;
  sizeBytes: number;
  /** See `<BulkResumeUpload />` for the state machine. */
  parseStatus: 'queued' | 'parsing' | 'parsed' | 'failed';
  parseError?: string;
  /** §29.C Stop semantics — marks this file as cancelled before scoring. */
  cancelled?: boolean;
}

export interface SmartMatchThread {
  id: string;
  /** Auth user id when the thread was created — for history All/Mine/Shared filter. */
  createdByUserId?: string;
  /** Display name — derived from the first prompt, user-editable in history. */
  name: string;
  /** The original prompt the user submitted from intake. */
  prompt: string;
  /** Which intake tab the user was on when the thread was created. */
  intakeTab: SmartMatchIntakeTab;
  /** Source pill at submission time (`Org database` default). */
  scopeId: string;
  /**
   * Collection state — null until the user picks a collection to write
   * shortlisted candidates into. Drives the `Save` vs `Shortlist` label per
   * §21.B (see `shortlistLabelFor`).
   */
  collection: SmartMatchCollectionRef | null;
  /** Candidate count surfaced in history list — best-effort, may be 0. */
  candidateCount: number;
  createdAt: number;
  updatedAt: number;
  /**
   * §29.A flow stage. Optional for backwards-compatibility with threads
   * created before T-25; `readStage()` defaults to `'results'` so existing
   * threads skip Stage 1 and land on the canvas as before.
   */
  stage?: SmartMatchStage;
  /**
   * §29.B — how many clarification rounds have been run. Caps at 2; the
   * 3rd paraphrase turn renders no question and only `[Start looking →]`.
   */
  clarificationRoundCount?: number;
  /**
   * §29.E / §29.F — the intake mode that created this thread. Optional
   * for backwards-compatibility; absent ⇒ `'single'`.
   */
  mode?: SmartMatchMode;
  /** §29.E — Multi-JD accordion uses the agentic flow by default. */
  manualReview?: boolean;
  /** §29.E — the list of JDs submitted with a Multi-JD thread. */
  jdList?: SmartMatchJdEntry[];
  /**
   * §29.E Stage 3 — which JD tab is currently active
   * (`'combined' | '<jd-id>'`). `'combined'` is the default.
   */
  activeJdTab?: string;
  /** §29.F — the uploaded-resume list for a Bulk thread. Metadata only. */
  resumeList?: SmartMatchResumeEntry[];
  /** §29.F — Role/JD context pasted in the bulk intake body. */
  bulkContextJd?: string;
  /**
   * §29.F Stage 3 — which bucket tab is active
   * (`'high' | 'medium' | 'low' | 'unparseable'`). `'high'` default.
   */
  activeBulkTab?: 'high' | 'medium' | 'low' | 'unparseable';
  /**
   * Addendum F §33.1 — which surface authored this thread. Absent ⇒
   * `'smart-match'` for backwards compatibility. Sourcing history reads
   * only threads where `origin === 'sourcing'`.
   */
  origin?: SmartMatchOrigin;
}

const LS_THREADS_KEY = 'ggj.smartmatch.threads';

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function isValidThread(t: unknown): t is SmartMatchThread {
  if (!t || typeof t !== 'object') return false;
  const r = t as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.name === 'string' &&
    typeof r.prompt === 'string' &&
    (r.intakeTab === 'job-description' || r.intakeTab === 'refine-search') &&
    typeof r.scopeId === 'string' &&
    (r.collection === null ||
      (typeof r.collection === 'object' &&
        r.collection != null &&
        typeof (r.collection as Record<string, unknown>).id === 'string' &&
        typeof (r.collection as Record<string, unknown>).name === 'string')) &&
    typeof r.candidateCount === 'number' &&
    typeof r.createdAt === 'number' &&
    typeof r.updatedAt === 'number' &&
    (typeof r.createdByUserId === 'string' || typeof r.createdByUserId === 'undefined')
    // `stage` + `clarificationRoundCount` are optional — see getStage().
  );
}

// ── §29.A Stage helpers ─────────────────────────────────────────────────────

/** Valid stage transitions; anything else is rejected (no-op). */
const STAGE_TRANSITIONS: Record<SmartMatchStage, SmartMatchStage[]> = {
  intake: ['clarify'],
  clarify: ['clarify', 'stream'],
  stream: ['results'],
  results: ['results', 'clarify'], // allow re-entering clarify for /update search
};

/**
 * Read the current stage of a thread. Threads created before T-25 have no
 * `stage` field — in that case we default to `'results'` so the old
 * behaviour (straight to the 3-column canvas) is preserved.
 */
export function getStage(id: string): SmartMatchStage {
  const t = getThread(id);
  return t?.stage ?? 'results';
}

/**
 * Transition a thread to a new stage. Validates the transition; returns
 * `false` when the move is illegal. Legal transitions:
 *   intake  → clarify
 *   clarify → clarify (re-clarifying)
 *   clarify → stream
 *   stream  → results
 *   results → clarify (via /update search)
 */
export function setStage(id: string, next: SmartMatchStage): boolean {
  const existing = getThread(id);
  if (!existing) return false;
  const current = existing.stage ?? 'intake';
  const allowed = STAGE_TRANSITIONS[current] ?? [];
  if (!allowed.includes(next)) return false;
  patchThread(id, { stage: next });
  return true;
}

/** Unconditional setter — only used by the dev sandbox and smoke tests. */
export function forceSetStage(id: string, next: SmartMatchStage): void {
  const existing = getThread(id);
  if (!existing) return;
  patchThread(id, { stage: next });
}

/**
 * Increment and return the new clarification round count. Caps at 2 (§29.B).
 */
export function incrementClarificationRound(id: string): number {
  const existing = getThread(id);
  if (!existing) return 0;
  const current = existing.clarificationRoundCount ?? 0;
  const next = Math.min(current + 1, 2);
  patchThread(id, { clarificationRoundCount: next });
  return next;
}

/** Read the current clarification round count (0 = no rounds yet). */
export function getClarificationRoundCount(id: string): number {
  return getThread(id)?.clarificationRoundCount ?? 0;
}

// ── §29.E / §29.F mode helpers ──────────────────────────────────────────────

/** Resolve the thread mode. Pre-T-26 threads without `mode` ⇒ `'single'`. */
export function getMode(id: string): SmartMatchMode {
  return getThread(id)?.mode ?? 'single';
}

/**
 * §29.C Stop semantics — mark the items that hadn't finished as
 * `cancelled`, preserving whatever did finish before Stop was pressed.
 *
 * - For Multi-JD, pass the set of JD ids that had completed scoring.
 * - For Bulk, pass the set of resume ids that had completed scoring.
 *
 * All other entries get `cancelled: true`; finished entries are left as-is.
 */
export function markCancelledRemainder(
  id: string,
  finishedIds: ReadonlySet<string>,
): void {
  const thread = getThread(id);
  if (!thread) return;
  const patch: Partial<Omit<SmartMatchThread, 'id' | 'createdAt'>> = {};
  if (thread.mode === 'multi' && thread.jdList) {
    patch.jdList = thread.jdList.map((j) =>
      finishedIds.has(j.id) ? { ...j, cancelled: false } : { ...j, cancelled: true },
    );
  } else if (thread.mode === 'bulk' && thread.resumeList) {
    patch.resumeList = thread.resumeList.map((r) =>
      finishedIds.has(r.id) ? { ...r, cancelled: false } : { ...r, cancelled: true },
    );
  }
  if (Object.keys(patch).length > 0) patchThread(id, patch);
}

export function readThreads(): SmartMatchThread[] {
  const s = safeStorage();
  if (!s) return [];
  try {
    const raw = s.getItem(LS_THREADS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidThread).sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export function writeThreads(threads: SmartMatchThread[]): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(LS_THREADS_KEY, JSON.stringify(threads));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ggj:smartmatch-threads-changed'));
    }
  } catch {
    /* quota / private mode — silently no-op */
  }
}

export function getThread(id: string): SmartMatchThread | null {
  if (!id) return null;
  return readThreads().find((t) => t.id === id) ?? null;
}

export function upsertThread(thread: SmartMatchThread): void {
  const list = readThreads();
  const idx = list.findIndex((t) => t.id === thread.id);
  if (idx === -1) list.push(thread);
  else list[idx] = thread;
  writeThreads(list);
}

export function patchThread(
  id: string,
  patch: Partial<Omit<SmartMatchThread, 'id' | 'createdAt'>>,
): SmartMatchThread | null {
  const existing = getThread(id);
  if (!existing) return null;
  const next: SmartMatchThread = {
    ...existing,
    ...patch,
    id: existing.id,
    createdAt: existing.createdAt,
    updatedAt: Date.now(),
  };
  upsertThread(next);
  return next;
}

export function renameThread(id: string, name: string): SmartMatchThread | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  return patchThread(id, { name: trimmed });
}

export function duplicateThread(id: string, newId: string): SmartMatchThread | null {
  const src = getThread(id);
  if (!src) return null;
  const copy: SmartMatchThread = {
    ...src,
    id: newId,
    name: `${src.name} (copy)`,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  upsertThread(copy);
  return copy;
}

export function deleteThread(id: string): void {
  const list = readThreads().filter((t) => t.id !== id);
  writeThreads(list);
}

/** Derive a short thread name from a prompt — first five non-trivial words. */
export function threadNameFromPrompt(prompt: string): string {
  const trimmed = prompt.trim();
  if (!trimmed) return 'Untitled search';
  const words = trimmed
    .replace(/[\n\r]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 || /^[A-Z]/.test(w))
    .slice(0, 5);
  const name = words.length > 0 ? words.join(' ') : trimmed.slice(0, 40);
  return name.length > 48 ? `${name.slice(0, 45)}…` : name;
}
